<script setup>
import axios from 'axios';
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

import { useAlert } from 'dashboard/composables';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { INBOX_TYPES } from 'dashboard/helper/inbox';

import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import BankMcpDocumentation from './BankMcpDocumentation.vue';

const route = useRoute();
const store = useStore();
const getters = useStoreGetters();

const selectedInboxId = ref('');
const apiKey = ref('');
const phoneNumber = ref('');
const contactName = ref('');
const messageContent = ref('Mensagem de teste enviada pela API da Talkvia.');
const conversationStatus = ref('open');
const hasConfirmedRealSend = ref(false);
const isSending = ref(false);
const testResponse = ref(null);
const externalContactName = ref('');
const externalContactPhone = ref('');
const externalContactEmail = ref('');
const externalContactIdentifier = ref('');
const hasConfirmedContactCreation = ref(false);
const isCreatingContact = ref(false);
const contactResponse = ref(null);
const cpfQueryValue = ref('');
const hasConfirmedCpfQuery = ref(false);
const isConsultingCpf = ref(false);
const cpfQueryResponse = ref(null);
const integrationMode = ref('rest');
const activeEndpoint = ref('contacts');
const activeMessageSection = ref('overview');
const activeExample = ref('curl');
const selectedInteractiveExample = ref('0');

const accountId = computed(() => route.params.accountId);
const apiHost = window.location.host;
const endpoint = computed(
  () =>
    `${window.location.origin}/api/v1/accounts/${accountId.value}/external_messages`
);
const contactEndpoint = computed(
  () =>
    `${window.location.origin}/api/v1/accounts/${accountId.value}/external_contacts`
);
const smsEndpoint = computed(
  () =>
    `${window.location.origin}/api/v1/accounts/${accountId.value}/external_sms`
);
const smsStatusEndpoint = computed(() => `${smsEndpoint.value}/{public_id}`);
const cpfQueryEndpoints = computed(() => ({
  lemit: `${window.location.origin}/api/v1/accounts/${accountId.value}/external_consulta_plus_queries`,
  nova_era: `${window.location.origin}/api/v1/accounts/${accountId.value}/external_contatos_queries`,
}));
const activeCpfProvider = computed(() =>
  activeEndpoint.value === 'nova_era' ? 'nova_era' : 'lemit'
);
const activeCpfProviderName = computed(() =>
  activeCpfProvider.value === 'nova_era'
    ? 'Talkvia Contatos'
    : 'Talkvia Consulta Plus'
);
const activeCpfEndpoint = computed(
  () => cpfQueryEndpoints.value[activeCpfProvider.value]
);
const allInboxes = computed(() => getters['inboxes/getInboxes'].value || []);
const inboxUIFlags = computed(() => getters['inboxes/getUIFlags'].value || {});
const isQrCodeInbox = inbox =>
  inbox?.channel_type === INBOX_TYPES.API &&
  inbox.additional_attributes?.is_evolution_api_integration === true;
const compatibleInboxes = computed(() =>
  allInboxes.value.filter(
    inbox =>
      isQrCodeInbox(inbox) ||
      [INBOX_TYPES.WHATSAPP, INBOX_TYPES.TWILIO, INBOX_TYPES.SMS].includes(
        inbox.channel_type
      )
  )
);

const channelLabel = inbox => {
  if (isQrCodeInbox(inbox)) return 'WhatsApp · QR Code';
  if (inbox.channel_type === INBOX_TYPES.WHATSAPP) return 'WhatsApp';
  if (inbox.channel_type === INBOX_TYPES.TWILIO) {
    return inbox.medium === 'sms' ? 'Twilio SMS' : 'Twilio WhatsApp';
  }
  return 'SMS';
};

const inboxOptions = computed(() =>
  compatibleInboxes.value.map(inbox => ({
    value: inbox.id,
    label: `${inbox.name} · ID ${inbox.id} · ${channelLabel(inbox)}`,
  }))
);
const selectedInbox = computed(() =>
  compatibleInboxes.value.find(
    inbox => inbox.id === Number(selectedInboxId.value)
  )
);
const exampleInboxId = computed(() => selectedInboxId.value || 'INBOX_ID');

const fields = [
  [
    'api_access_token',
    'header · obrigatório',
    'API key de um usuário com acesso à conta e à caixa escolhida.',
  ],
  [
    'inbox_id',
    'number · condicional',
    'ID da caixa WhatsApp/SMS. Só pode ser omitido quando há uma única caixa compatível.',
  ],
  [
    'phone_number',
    'string · obrigatório',
    'Telefone internacional com código do país e DDD, como +5511999999999.',
  ],
  [
    'name',
    'string · opcional',
    'Nome usado ao criar o contato. Se omitido, o telefone será usado como nome.',
  ],
  [
    'content',
    'string · condicional',
    'Texto da mensagem. Obrigatório quando template_params não for enviado.',
  ],
  [
    'content_type',
    'string · opcional',
    'Tipo do conteúdo. Use whatsapp_interactive para mensagens interativas; o padrão é text.',
  ],
  [
    'content_attributes',
    'object · condicional',
    'Obrigatório para mensagens interativas. Envie a configuração em content_attributes.interactive.',
  ],
  ['status', 'string · opcional', 'Status da conversa. O valor padrão é open.'],
  [
    'template_params',
    'object · condicional',
    'Template oficial do WhatsApp com nome, idioma e parâmetros processados.',
  ],
].map(([name, type, description]) => ({ name, type, description }));

const responseFields = [
  'message_id',
  'message_status',
  'contact_id',
  'contact_inbox_id',
  'conversation_id',
  'inbox_id',
  'phone_number',
];
const statusOptions = [
  { value: 'open', label: 'Aberta (open)' },
  { value: 'pending', label: 'Pendente (pending)' },
];
const responseCodes = [
  ['201', 'Mensagem criada e encaminhada para envio.'],
  ['400', 'Payload inválido, campo obrigatório ausente ou caixa incompatível.'],
  ['401', 'API key ausente ou inválida.'],
  ['403', 'O usuário não possui acesso à conta ou à caixa selecionada.'],
  ['404', 'Conta ou caixa não encontrada.'],
];
const contactResponseCodes = [
  ['201', 'Contato cadastrado com sucesso.'],
  ['400', 'Nome ou telefone não informado.'],
  ['401', 'API key ausente ou inválida.'],
  ['422', 'Telefone, e-mail ou identificador inválido ou já cadastrado.'],
];
const contactResponseFields = [
  'id',
  'name',
  'phone_number',
  'email',
  'identifier',
  'custom_attributes',
  'additional_attributes',
  'created_at',
];
const contactFields = [
  ['name', 'string · obrigatório', 'Nome completo do contato.'],
  [
    'phone_number',
    'string · obrigatório',
    'Telefone com código do país e DDD. Formatações visuais são aceitas e o valor é normalizado para E.164.',
  ],
  ['email', 'string · opcional', 'E-mail válido e único dentro da conta.'],
  [
    'identifier',
    'string · opcional',
    'Identificador externo único dentro da conta, como o ID do seu CRM.',
  ],
  [
    'custom_attributes',
    'object · opcional',
    'Atributos personalizados já configurados para contatos.',
  ],
  [
    'additional_attributes',
    'object · opcional',
    'Metadados adicionais do contato.',
  ],
].map(([name, type, description]) => ({ name, type, description }));
const smsFields = [
  [
    'message',
    'string · obrigatório',
    'Conteúdo do SMS. Espaços nas extremidades são removidos.',
  ],
  [
    'recipient',
    'string · condicional',
    'Celular brasileiro para envio individual, com ou sem o código 55 e formatação.',
  ],
  [
    'recipients',
    'array · condicional',
    'Lista de até 1.000 celulares. Use este campo ou recipient; números repetidos são enviados uma única vez.',
  ],
  [
    'excluded_labels',
    'array · opcional',
    'Nomes de etiquetas da própria conta. Qualquer correspondência no contato ou em suas conversas bloqueia o envio, sem diferenciar maiúsculas. Padrão: []. Exclusão antes da cobrança e nova conferência na saída da fila.',
  ],
].map(([name, type, description]) => ({ name, type, description }));
const smsResponseCodes = [
  ['202', 'Campanha aceita, créditos reservados e envio colocado na fila.'],
  ['401', 'API key ausente ou inválida.'],
  ['403', 'O usuário não possui acesso à conta.'],
  ['404', 'Campanha não encontrada ao consultar o status.'],
  ['422', 'Mensagem, destinatários, configuração ou saldo inválido.'],
];
const smsResponseFields = [
  'public_id',
  'status',
  'message_encoding',
  'message_units',
  'message_segments',
  'total_recipients',
  'credits_reserved',
  'credits_consumed',
  'counts',
  'recipients',
];
const cpfQueryFields = [
  [
    'cpf',
    'string · obrigatório',
    'CPF válido com 11 dígitos. Pontos e traço são aceitos e removidos antes da consulta.',
  ],
].map(([name, type, description]) => ({ name, type, description }));
const cpfQueryResponseCodes = [
  ['201', 'Consulta concluída e resultado retornado.'],
  ['401', 'API key ausente ou inválida.'],
  ['403', 'O usuário não possui acesso às consultas de CPF.'],
  ['422', 'CPF, produto, saldo ou configuração do provedor inválido.'],
];
const cpfQueryResponseFields = [
  'consultation_id',
  'provider',
  'provider_name',
  'status',
  'cpf',
  'credits_used',
  'contact_id',
  'result',
  'created_at',
  'finished_at',
];

const normalizedMessagePhone = computed(() => {
  const digits = phoneNumber.value.replace(/\D/g, '');
  return digits ? `+${digits}` : '';
});
const simplePayload = computed(() => ({
  inbox_id: exampleInboxId.value,
  phone_number: normalizedMessagePhone.value || '+5511999999999',
  name: contactName.value || 'Maria Silva',
  content: messageContent.value || 'Olá, tudo bem?',
  status: conversationStatus.value,
}));
const testPayload = computed(() => ({
  inbox_id: Number(selectedInboxId.value),
  phone_number: normalizedMessagePhone.value,
  ...(contactName.value.trim() ? { name: contactName.value.trim() } : {}),
  content: messageContent.value.trim(),
  status: conversationStatus.value,
}));
const templatePayload = computed(() => ({
  inbox_id: exampleInboxId.value,
  phone_number: '+5511999999999',
  name: 'Maria Silva',
  content: 'Template enviado',
  template_params: {
    name: 'pedido_atualizado',
    language: 'pt_BR',
    processed_params: { body: { 1: 'Maria', 2: '12345' } },
  },
}));
const interactiveInboxId = computed(() =>
  selectedInbox.value?.channel_type === INBOX_TYPES.WHATSAPP &&
  selectedInbox.value?.provider === 'whatsapp_cloud'
    ? selectedInbox.value.id
    : 'WHATSAPP_CLOUD_INBOX_ID'
);
const interactivePayloads = computed(() => {
  const base = {
    inbox_id: interactiveInboxId.value,
    phone_number: '+5511999999999',
    name: 'Maria Silva',
    content_type: 'whatsapp_interactive',
    status: 'open',
  };

  return [
    {
      title: 'Pagamento Pix (sem template)',
      description:
        'Somente dentro da janela de atendimento, em WhatsApp Cloud com números brasileiros. Use uma cobrança real do banco; o envio não confirma pagamento.',
      payload: {
        ...base,
        content:
          'Confira sua cobrança e copie o código Pix para pagar no aplicativo do banco.',
        content_attributes: {
          interactive: {
            type: 'order_details',
            reference_id: 'COBRANCA_123',
            amount: '150.00',
            description: 'Descrição da cobrança',
            pix_code: 'SUBSTITUA_PELO_PIX_COPIA_E_COLA_COMPLETO_DO_BANCO',
            merchant_name: 'Nome do recebedor cadastrado no banco',
            pix_key: 'CHAVE_PIX_DO_RECEBEDOR',
            pix_key_type: 'CNPJ',
          },
        },
      },
    },
    {
      title: 'Botões de resposta',
      description:
        'Até três respostas rápidas. O id deve acompanhar o texto visível.',
      payload: {
        ...base,
        content: 'Escolha uma opção:',
        content_attributes: {
          interactive: {
            type: 'button',
            header_type: 'text',
            header_text: 'Atendimento',
            footer_text: 'Talkvia',
            buttons: [
              { id: 'Confirmar', title: 'Confirmar' },
              { id: 'Cancelar', title: 'Cancelar' },
            ],
          },
        },
      },
    },
    {
      title: 'Solicitação de localização',
      description:
        'Exibe no WhatsApp a ação nativa para compartilhar a localização atual.',
      payload: {
        ...base,
        content: 'Compartilhe sua localização atual:',
        content_attributes: {
          interactive: { type: 'location_request_message' },
        },
      },
    },
    {
      title: 'Link de ação',
      description: 'Exibe um botão que abre uma URL HTTPS.',
      payload: {
        ...base,
        content: 'Acesse nosso site:',
        content_attributes: {
          interactive: {
            type: 'cta_url',
            cta_display_text: 'Abrir Talkvia',
            cta_url: 'https://talkvia.com.br',
          },
        },
      },
    },
    {
      title: 'WhatsApp Flow',
      description:
        'Abre um Flow publicado na Meta usando o identificador e o token informados.',
      payload: {
        ...base,
        content: 'Preencha os dados para continuar:',
        content_attributes: {
          interactive: {
            type: 'flow',
            flow_id: 'FLOW_ID',
            flow_token: 'FLOW_TOKEN',
            flow_cta: 'Continuar',
            flow_action: 'navigate',
            flow_screen: 'INICIO',
            flow_data: { origem: 'api' },
          },
        },
      },
    },
    {
      title: 'Catálogo',
      description: 'Abre o catálogo vinculado ao número do WhatsApp Business.',
      payload: {
        ...base,
        content: 'Confira nosso catálogo:',
        content_attributes: {
          interactive: {
            type: 'catalog_message',
            catalog_thumbnail_product_retailer_id: 'CODIGO_DO_PRODUTO',
          },
        },
      },
    },
  ];
});
const interactiveOptions = computed(() =>
  interactivePayloads.value.map((example, index) => ({
    value: String(index),
    label: example.title,
  }))
);
const currentInteractiveExample = computed(
  () =>
    interactivePayloads.value[Number(selectedInteractiveExample.value)] ||
    interactivePayloads.value[0]
);

const messageSections = [
  { value: 'overview', label: 'Visão geral', icon: 'i-lucide-layout-list' },
  { value: 'examples', label: 'Exemplos', icon: 'i-lucide-code-2' },
  { value: 'console', label: 'Testar API', icon: 'i-lucide-terminal' },
  { value: 'responses', label: 'Respostas', icon: 'i-lucide-activity' },
];
const exampleTabs = [
  { value: 'curl', label: 'cURL' },
  { value: 'javascript', label: 'JavaScript' },
  { value: 'template', label: 'Template WhatsApp' },
  { value: 'interactive', label: 'Interativa' },
];

const shellQuote = value => `'${value.replaceAll("'", `'"'"'`)}'`;
const curlExample = computed(() =>
  [
    `curl --request POST ${shellQuote(endpoint.value)} \\`,
    '  --header "api_access_token: USER_API_KEY" \\',
    '  --header "Content-Type: application/json" \\',
    `  --data-raw ${shellQuote(JSON.stringify(simplePayload.value, null, 2))}`,
  ].join('\n')
);
const javascriptExample = computed(() =>
  [
    `const response = await fetch('${endpoint.value}', {`,
    "  method: 'POST',",
    '  headers: {',
    "    api_access_token: 'USER_API_KEY',",
    "    'Content-Type': 'application/json',",
    '  },',
    `  body: JSON.stringify(${JSON.stringify(simplePayload.value, null, 2)
      .split('\n')
      .join('\n  ')}),`,
    '});',
    'const result = await response.json();',
  ].join('\n')
);
const normalizedContactPhone = computed(() => {
  const digits = externalContactPhone.value.replace(/\D/g, '');
  return digits ? `+${digits}` : '';
});
const contactPayload = computed(() => ({
  name: externalContactName.value.trim() || 'Maria Silva',
  phone_number: normalizedContactPhone.value || '+5511999999999',
  ...(externalContactEmail.value.trim()
    ? { email: externalContactEmail.value.trim() }
    : {}),
  ...(externalContactIdentifier.value.trim()
    ? { identifier: externalContactIdentifier.value.trim() }
    : {}),
}));
const contactCurlExample = computed(() =>
  [
    `curl --request POST ${shellQuote(contactEndpoint.value)} \\`,
    '  --header "api_access_token: USER_API_KEY" \\',
    '  --header "Content-Type: application/json" \\',
    `  --data-raw ${shellQuote(JSON.stringify(contactPayload.value, null, 2))}`,
  ].join('\n')
);
const smsPayload = computed(() => ({
  message:
    'Talkvia: sua solicitação foi recebida. Em caso de dúvidas, responda por nossos canais oficiais.',
  recipients: ['5511999999999', '11988888888'],
  excluded_labels: [],
}));
const smsCurlExample = computed(() =>
  [
    `curl --request POST ${shellQuote(smsEndpoint.value)} \\`,
    '  --header "api_access_token: USER_API_KEY" \\',
    '  --header "Content-Type: application/json" \\',
    `  --data-raw ${shellQuote(JSON.stringify(smsPayload.value, null, 2))}`,
  ].join('\n')
);
const smsRetryCurlExample = computed(() =>
  [
    `curl --request POST ${shellQuote(`${smsStatusEndpoint.value}/retry_failed`)} \\`,
    '  --header "api_access_token: USER_API_KEY" \\',
    '  --header "Content-Type: application/json" \\',
    `  --data-raw '${JSON.stringify({ recipient_id: 'PUBLIC_ID_DO_DESTINATARIO' })}'`,
  ].join('\n')
);
const smsStatusCurlExample = computed(() =>
  [
    `curl --request GET ${shellQuote(smsStatusEndpoint.value)} \\`,
    '  --header "api_access_token: USER_API_KEY"',
  ].join('\n')
);
const normalizedCpfQuery = computed(() =>
  cpfQueryValue.value.replace(/\D/g, '').slice(0, 11)
);
const cpfQueryPayload = computed(() => ({
  cpf: normalizedCpfQuery.value || '03993691393',
}));
const cpfQueryCurlExample = computed(() =>
  [
    `curl --request POST ${shellQuote(activeCpfEndpoint.value)} \\`,
    '  --header "api_access_token: USER_API_KEY" \\',
    '  --header "Content-Type: application/json" \\',
    `  --data-raw ${shellQuote(JSON.stringify(cpfQueryPayload.value, null, 2))}`,
  ].join('\n')
);
const activeExampleCode = computed(() => {
  if (activeExample.value === 'javascript') return javascriptExample.value;
  if (activeExample.value === 'template') {
    return JSON.stringify(templatePayload.value, null, 2);
  }
  if (activeExample.value === 'interactive') {
    return JSON.stringify(currentInteractiveExample.value.payload, null, 2);
  }
  return curlExample.value;
});
const activeExampleTitle = computed(() => {
  if (activeExample.value === 'javascript') return 'JavaScript · fetch';
  if (activeExample.value === 'template') return 'JSON · template oficial';
  if (activeExample.value === 'interactive') {
    return `JSON · ${currentInteractiveExample.value.title}`;
  }
  return 'cURL · mensagem simples';
});
const canSendTest = computed(
  () =>
    selectedInboxId.value &&
    apiKey.value.trim() &&
    phoneNumber.value.trim() &&
    messageContent.value.trim() &&
    hasConfirmedRealSend.value &&
    !isSending.value
);
const canCreateContact = computed(
  () =>
    apiKey.value.trim() &&
    externalContactName.value.trim() &&
    normalizedContactPhone.value.length >= 9 &&
    hasConfirmedContactCreation.value &&
    !isCreatingContact.value
);
const canConsultCpf = computed(
  () =>
    apiKey.value.trim() &&
    normalizedCpfQuery.value.length === 11 &&
    hasConfirmedCpfQuery.value &&
    !isConsultingCpf.value
);

const maskContactPhone = value => {
  const digits = String(value || '')
    .replace(/\D/g, '')
    .slice(0, 15);
  if (!digits) return '';
  if (!digits.startsWith('55')) return `+${digits}`;

  const country = digits.slice(0, 2);
  const area = digits.slice(2, 4);
  const local = digits.slice(4);
  const firstPart = local.length > 8 ? local.slice(0, 5) : local.slice(0, 4);
  const secondPart = local.slice(firstPart.length, firstPart.length + 4);
  return `+${country}${area ? ` (${area}` : ''}${area.length === 2 ? ')' : ''}${
    firstPart ? ` ${firstPart}` : ''
  }${secondPart ? `-${secondPart}` : ''}`;
};

const updateContactPhone = value => {
  externalContactPhone.value = maskContactPhone(value);
  contactResponse.value = null;
};

const updateMessagePhone = value => {
  phoneNumber.value = maskContactPhone(value);
  testResponse.value = null;
};

watch(
  compatibleInboxes,
  inboxes => {
    const selectionExists = inboxes.some(
      inbox => inbox.id === Number(selectedInboxId.value)
    );
    if (!selectionExists) selectedInboxId.value = inboxes[0]?.id || '';
  },
  { immediate: true }
);
watch(selectedInboxId, () => {
  testResponse.value = null;
});

const copyCode = async (value, label) => {
  try {
    await navigator.clipboard.writeText(value);
    useAlert(`${label} copiado.`, { variant: 'positive' });
  } catch {
    useAlert(`Não foi possível copiar o ${label.toLowerCase()}.`);
  }
};

const sendTestMessage = async () => {
  if (!canSendTest.value) return;
  isSending.value = true;
  testResponse.value = null;
  try {
    const response = await axios.post(endpoint.value, testPayload.value, {
      headers: {
        api_access_token: apiKey.value.trim(),
        'Content-Type': 'application/json',
      },
    });
    testResponse.value = {
      ok: true,
      status: response.status,
      data: response.data,
    };
    useAlert('Mensagem de teste criada com sucesso.', { variant: 'positive' });
  } catch (error) {
    testResponse.value = {
      ok: false,
      status: error.response?.status || null,
      data: error.response?.data || {
        message: error.message || 'Não foi possível alcançar a API.',
      },
    };
    useAlert('A API recusou o envio. Confira a resposta exibida na tela.');
  } finally {
    isSending.value = false;
  }
};

const createTestContact = async () => {
  if (!canCreateContact.value) return;
  isCreatingContact.value = true;
  contactResponse.value = null;
  try {
    const response = await axios.post(
      contactEndpoint.value,
      contactPayload.value,
      {
        headers: {
          api_access_token: apiKey.value.trim(),
          'Content-Type': 'application/json',
        },
      }
    );
    contactResponse.value = {
      ok: true,
      status: response.status,
      data: response.data,
    };
    useAlert('Contato externo cadastrado com sucesso.', {
      variant: 'positive',
    });
  } catch (error) {
    contactResponse.value = {
      ok: false,
      status: error.response?.status || null,
      data: error.response?.data || {
        message: error.message || 'Não foi possível alcançar a API.',
      },
    };
    useAlert('A API recusou o cadastro. Confira a resposta exibida na tela.');
  } finally {
    isCreatingContact.value = false;
  }
};

const consultCpf = async () => {
  if (!canConsultCpf.value) return;
  isConsultingCpf.value = true;
  cpfQueryResponse.value = null;
  try {
    const response = await axios.post(
      activeCpfEndpoint.value,
      { cpf: normalizedCpfQuery.value },
      {
        headers: {
          api_access_token: apiKey.value.trim(),
          'Content-Type': 'application/json',
        },
      }
    );
    cpfQueryResponse.value = {
      ok: true,
      status: response.status,
      data: response.data,
    };
    useAlert(`Consulta ${activeCpfProviderName.value} concluída.`, {
      variant: 'positive',
    });
  } catch (error) {
    cpfQueryResponse.value = {
      ok: false,
      status: error.response?.status || null,
      data: error.response?.data || {
        message: error.message || 'Não foi possível alcançar a API.',
      },
    };
    useAlert('A consulta não foi concluída. Confira a resposta da API.');
  } finally {
    isConsultingCpf.value = false;
  }
};

watch(activeCpfProvider, () => {
  cpfQueryResponse.value = null;
  hasConfirmedCpfQuery.value = false;
});

onMounted(() => store.dispatch('inboxes/get'));
</script>

<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text, vue/max-attributes-per-line, vue/html-closing-bracket-newline -->
<template>
  <SettingsLayout>
    <template #header>
      <BaseSettingsHeader
        title="Documentação API"
        description="Integre seus sistemas à Talkvia com endpoints prontos, exemplos copiáveis e consoles de teste seguros."
      />
    </template>

    <template #body>
      <section class="api-page">
        <header class="api-hero">
          <div class="min-w-0 flex-1">
            <span class="eyebrow">Talkvia Developers</span>
            <h1>Integrações para conectar sua operação</h1>
            <p>
              Escolha as APIs convencionais para conectar sistemas ou conheça o
              MCP bancário para integrar assistentes de IA à sua operação.
            </p>
          </div>
          <div class="base-url-card">
            <span>Ambiente atual</span>
            <div>
              <span class="i-lucide-radio size-4 text-n-teal-10" />
              <code>{{ apiHost }}</code>
            </div>
          </div>
        </header>

        <nav class="grid gap-3 sm:grid-cols-2" aria-label="Tipo de integração">
          <button
            type="button"
            class="flex items-start gap-3 rounded-xl border p-4 text-left transition"
            :class="
              integrationMode === 'rest'
                ? 'border-n-brand bg-n-brand/10'
                : 'border-n-weak bg-n-solid-1'
            "
            :aria-pressed="integrationMode === 'rest'"
            data-testid="integration-rest"
            @click="integrationMode = 'rest'"
          >
            <span
              class="grid size-10 shrink-0 place-items-center rounded-xl bg-n-brand/10 text-n-brand"
              aria-hidden="true"
            >
              <span class="i-hugeicons-plug-01 size-6" />
            </span>
            <span class="min-w-0">
              <strong class="block text-base text-n-slate-12"
                >Integrações convencionais · API REST</strong
              >
              <span class="mt-1 block text-sm text-n-slate-11"
                >Contatos, mensagens, SMS e consultas por CPF para seus
                sistemas.</span
              >
            </span>
          </button>
          <button
            type="button"
            class="flex items-start gap-3 rounded-xl border p-4 text-left transition"
            :class="
              integrationMode === 'mcp'
                ? 'border-n-brand bg-n-brand/10'
                : 'border-n-weak bg-n-solid-1'
            "
            :aria-pressed="integrationMode === 'mcp'"
            data-testid="integration-mcp"
            @click="integrationMode = 'mcp'"
          >
            <span
              class="grid size-10 shrink-0 place-items-center rounded-xl bg-n-brand/10 text-n-brand"
              aria-hidden="true"
            >
              <span class="i-hugeicons-ai-brain-01 size-6" />
            </span>
            <span class="min-w-0">
              <strong class="block text-base text-n-slate-12"
                >MCP dos bancos · Assistentes de IA</strong
              >
              <span class="mt-1 block text-sm text-n-slate-11"
                >Entenda os benefícios e siga o guia para conectar seu
                assistente.</span
              >
            </span>
          </button>
        </nav>
        <BankMcpDocumentation
          v-if="integrationMode === 'mcp'"
          :account-id="accountId"
        />
        <div
          v-show="integrationMode === 'rest'"
          class="flex min-w-0 flex-col gap-5"
          data-testid="rest-documentation"
        >
          <div class="api-meta-bar">
            <div class="meta-item">
              <span class="i-lucide-shield-check size-4 text-n-teal-10" />
              <span><strong>Autenticação</strong> API key por usuário</span>
            </div>
            <div class="meta-item">
              <span class="i-lucide-braces size-4 text-n-blue-10" />
              <span><strong>Conteúdo</strong> JSON UTF-8</span>
            </div>
            <div class="meta-item">
              <span class="i-lucide-lock-keyhole size-4 text-n-amber-10" />
              <span><strong>Transporte</strong> HTTPS em produção</span>
            </div>
          </div>

          <div class="security-note">
            <span class="i-lucide-key-round mt-0.5 size-5 shrink-0" />
            <p>
              Nas APIs REST, envie a chave no header
              <code>api_access_token</code>. Nunca exponha a credencial em
              sites, aplicativos distribuídos ou repositórios públicos; mantenha
              as chamadas no seu backend.
            </p>
          </div>

          <div class="docs-workspace">
            <aside class="endpoint-sidebar">
              <div class="sidebar-heading">
                <span>Endpoints</span>
                <small>5 disponíveis</small>
              </div>
              <button
                type="button"
                class="endpoint-selector"
                :class="{ active: activeEndpoint === 'lemit' }"
                @click="activeEndpoint = 'lemit'"
              >
                <span class="endpoint-icon i-lucide-contact-round" />
                <span class="min-w-0 flex-1">
                  <strong>Consultar Talkvia Consulta Plus</strong>
                  <small>/external_consulta_plus_queries</small>
                </span>
                <span class="method-badge">POST</span>
              </button>
              <button
                type="button"
                class="endpoint-selector"
                :class="{ active: activeEndpoint === 'nova_era' }"
                @click="activeEndpoint = 'nova_era'"
              >
                <span class="endpoint-icon i-lucide-scan-search" />
                <span class="min-w-0 flex-1">
                  <strong>Consultar Talkvia Contatos</strong>
                  <small>/external_contatos_queries</small>
                </span>
                <span class="method-badge">POST</span>
              </button>
              <button
                type="button"
                class="endpoint-selector"
                :class="{ active: activeEndpoint === 'contacts' }"
                @click="activeEndpoint = 'contacts'"
              >
                <span class="endpoint-icon i-lucide-user-plus" />
                <span class="min-w-0 flex-1">
                  <strong>Cadastrar contato</strong>
                  <small>/external_contacts</small>
                </span>
                <span class="method-badge">POST</span>
              </button>
              <button
                type="button"
                class="endpoint-selector"
                :class="{ active: activeEndpoint === 'sms' }"
                @click="activeEndpoint = 'sms'"
              >
                <span class="endpoint-icon i-lucide-message-circle-more" />
                <span class="min-w-0 flex-1">
                  <strong>Disparar SMS</strong>
                  <small>/external_sms</small>
                </span>
                <span class="method-badge">POST</span>
              </button>
              <button
                type="button"
                class="endpoint-selector"
                :class="{ active: activeEndpoint === 'messages' }"
                @click="activeEndpoint = 'messages'"
              >
                <span class="endpoint-icon i-lucide-message-square-share" />
                <span class="min-w-0 flex-1">
                  <strong>Enviar mensagem</strong>
                  <small>/external_messages</small>
                </span>
                <span class="method-badge">POST</span>
              </button>

              <div class="quick-guide">
                <span class="eyebrow">Primeiros passos</span>
                <ol>
                  <li><span>1</span>Escolha o endpoint</li>
                  <li><span>2</span>Copie e adapte o exemplo</li>
                  <li><span>3</span>Valide no console seguro</li>
                </ol>
              </div>
            </aside>

            <main class="min-w-0">
              <div
                v-if="activeEndpoint === 'contacts'"
                class="endpoint-content"
              >
                <header class="endpoint-header">
                  <div class="endpoint-title-row">
                    <span class="endpoint-large-icon i-lucide-user-plus" />
                    <div class="min-w-0">
                      <div class="mb-1 flex flex-wrap items-center gap-2">
                        <span class="method-badge">POST</span>
                        <span class="status-badge">Disponível</span>
                      </div>
                      <h2>Cadastro externo de contato</h2>
                      <p>
                        Crie contatos a partir do seu CRM, site ou sistema de
                        gestão. Nome e telefone são obrigatórios.
                      </p>
                    </div>
                  </div>
                  <div class="endpoint-row">
                    <code class="min-w-0 flex-1 break-all">
                      {{ contactEndpoint }}
                    </code>
                    <Button
                      label="Copiar URL"
                      icon="i-lucide-copy"
                      variant="ghost"
                      color="slate"
                      size="sm"
                      @click="copyCode(contactEndpoint, 'URL')"
                    />
                  </div>
                </header>

                <div class="content-grid">
                  <section class="doc-card">
                    <div class="section-heading">
                      <span class="section-number">01</span>
                      <div>
                        <h3>Corpo da requisição</h3>
                        <p>Campos aceitos pelo endpoint.</p>
                      </div>
                    </div>
                    <div class="field-list">
                      <div
                        v-for="field in contactFields"
                        :key="field.name"
                        class="field-item"
                      >
                        <div>
                          <code>{{ field.name }}</code>
                          <span
                            :class="{
                              required: field.type.includes('obrigatório'),
                            }"
                            >{{ field.type }}</span
                          >
                        </div>
                        <p>{{ field.description }}</p>
                      </div>
                    </div>
                  </section>

                  <section class="code-card">
                    <div class="code-card-header">
                      <div>
                        <span class="eyebrow">Exemplo pronto</span>
                        <h3>cURL</h3>
                      </div>
                      <Button
                        label="Copiar"
                        icon="i-lucide-copy"
                        variant="ghost"
                        color="slate"
                        size="sm"
                        @click="copyCode(contactCurlExample, 'cURL')"
                      />
                    </div>
                    <pre
                      class="code-block"
                    ><code>{{ contactCurlExample }}</code></pre>
                    <p class="code-hint">
                      <span class="i-lucide-info size-4" />
                      O telefone é normalizado para o padrão internacional
                      E.164.
                    </p>
                  </section>
                </div>

                <section class="console-card">
                  <div class="section-heading">
                    <span class="section-number">02</span>
                    <div>
                      <h3>Testar cadastro</h3>
                      <p>
                        Faça uma chamada real. A API key não é armazenada pela
                        página.
                      </p>
                    </div>
                    <span class="console-badge">
                      <span class="i-lucide-terminal size-3.5" /> Console
                    </span>
                  </div>
                  <div class="form-grid">
                    <Input
                      v-model="apiKey"
                      type="password"
                      label="API key do usuário"
                      placeholder="Cole a API key"
                      show-password-toggle
                      autocomplete="off"
                    />
                    <Input
                      v-model="externalContactName"
                      label="Nome *"
                      placeholder="Maria Silva"
                      autocomplete="off"
                    />
                    <Input
                      :model-value="externalContactPhone"
                      label="Telefone *"
                      placeholder="+55 (11) 99999-9999"
                      autocomplete="off"
                      message="Inclua o código do país e o DDD."
                      @update:model-value="updateContactPhone"
                    />
                    <Input
                      v-model="externalContactEmail"
                      type="email"
                      label="E-mail (opcional)"
                      placeholder="maria@empresa.com.br"
                      autocomplete="off"
                    />
                    <Input
                      v-model="externalContactIdentifier"
                      label="Identificador externo (opcional)"
                      placeholder="crm_12345"
                      autocomplete="off"
                    />
                  </div>
                  <label class="real-send-warning">
                    <input
                      v-model="hasConfirmedContactCreation"
                      type="checkbox"
                      class="mt-0.5 size-4 accent-n-brand"
                    />
                    <span>
                      Confirmo que o teste criará um contato real nesta conta.
                    </span>
                  </label>
                  <div class="action-row">
                    <Button
                      label="Cadastrar contato de teste"
                      icon="i-lucide-user-plus"
                      :is-loading="isCreatingContact"
                      :disabled="!canCreateContact"
                      @click="createTestContact"
                    />
                    <span>HTTP 201 no sucesso</span>
                  </div>
                  <div v-if="contactResponse" class="response-panel">
                    <div class="code-card-header">
                      <h3>Resposta da API</h3>
                      <span
                        :class="
                          contactResponse.ok ? 'success-badge' : 'error-badge'
                        "
                        >HTTP
                        {{ contactResponse.status || 'erro de rede' }}</span
                      >
                    </div>
                    <pre class="code-block max-h-80"><code>{{
                    JSON.stringify(contactResponse.data, null, 2)
                  }}</code></pre>
                  </div>
                </section>

                <section class="doc-card">
                  <div class="section-heading">
                    <span class="section-number">03</span>
                    <div>
                      <h3>Respostas</h3>
                      <p>Códigos HTTP e campos retornados.</p>
                    </div>
                  </div>
                  <div class="response-grid">
                    <div
                      v-for="item in contactResponseCodes"
                      :key="item[0]"
                      class="response-item"
                    >
                      <code>{{ item[0] }}</code>
                      <span>{{ item[1] }}</span>
                    </div>
                  </div>
                  <div class="field-chips">
                    <code v-for="field in contactResponseFields" :key="field">
                      {{ field }}
                    </code>
                  </div>
                </section>
              </div>

              <div
                v-else-if="['lemit', 'nova_era'].includes(activeEndpoint)"
                class="endpoint-content"
              >
                <header class="endpoint-header">
                  <div class="endpoint-title-row">
                    <span class="endpoint-large-icon i-lucide-scan-search" />
                    <div class="min-w-0">
                      <div class="mb-1 flex flex-wrap items-center gap-2">
                        <span class="method-badge">POST</span>
                        <span class="status-badge">Disponível</span>
                      </div>
                      <h2>Consulta externa {{ activeCpfProviderName }}</h2>
                      <p>
                        Consulte um CPF usando os créditos e a configuração de
                        {{ activeCpfProviderName }} da conta autenticada.
                      </p>
                    </div>
                  </div>
                  <div class="endpoint-row">
                    <code class="min-w-0 flex-1 break-all">
                      {{ activeCpfEndpoint }}
                    </code>
                    <Button
                      label="Copiar URL"
                      icon="i-lucide-copy"
                      variant="ghost"
                      color="slate"
                      size="sm"
                      @click="copyCode(activeCpfEndpoint, 'URL')"
                    />
                  </div>
                </header>

                <div class="content-grid">
                  <section class="doc-card">
                    <div class="section-heading">
                      <span class="section-number">01</span>
                      <div>
                        <h3>Corpo da requisição</h3>
                        <p>Informe o CPF que será consultado.</p>
                      </div>
                    </div>
                    <div class="field-list">
                      <div
                        v-for="field in cpfQueryFields"
                        :key="field.name"
                        class="field-item"
                      >
                        <div>
                          <code>{{ field.name }}</code>
                          <span class="required">{{ field.type }}</span>
                        </div>
                        <p>{{ field.description }}</p>
                      </div>
                    </div>
                    <div class="info-callout">
                      <span class="i-lucide-shield-check size-5" />
                      <p>
                        A API key deve pertencer a um usuário autorizado. Cada
                        chamada concluída consome os créditos configurados para
                        o produto {{ activeCpfProviderName }}.
                      </p>
                    </div>
                  </section>

                  <section class="code-card">
                    <div class="code-card-header">
                      <div>
                        <span class="eyebrow">Exemplo pronto</span>
                        <h3>cURL</h3>
                      </div>
                      <Button
                        label="Copiar"
                        icon="i-lucide-copy"
                        variant="ghost"
                        color="slate"
                        size="sm"
                        @click="copyCode(cpfQueryCurlExample, 'cURL')"
                      />
                    </div>
                    <pre
                      class="code-block"
                    ><code>{{ cpfQueryCurlExample }}</code></pre>
                    <p class="code-hint">
                      <span class="i-lucide-clock-3 size-4" />
                      A resposta é síncrona e inclui o resultado retornado pelo
                      provedor.
                    </p>
                  </section>
                </div>

                <section class="console-card">
                  <div class="section-heading">
                    <span class="section-number">02</span>
                    <div>
                      <h3>Testar consulta</h3>
                      <p>
                        Faça uma consulta real. A API key não é armazenada pela
                        página.
                      </p>
                    </div>
                    <span class="console-badge">
                      <span class="i-lucide-terminal size-3.5" /> Console
                    </span>
                  </div>
                  <div class="form-grid">
                    <Input
                      v-model="apiKey"
                      type="password"
                      label="API key do usuário"
                      placeholder="Cole a API key"
                      show-password-toggle
                      autocomplete="off"
                    />
                    <Input
                      v-model="cpfQueryValue"
                      label="CPF"
                      placeholder="000.000.000-00"
                      autocomplete="off"
                      message="Use um CPF válido com 11 dígitos."
                      :max-length="14"
                    />
                  </div>
                  <label class="real-send-warning">
                    <input
                      v-model="hasConfirmedCpfQuery"
                      type="checkbox"
                      class="mt-0.5 size-4 accent-n-brand"
                    />
                    <span>
                      Confirmo que esta consulta é real e consumirá créditos de
                      {{ activeCpfProviderName }}.
                    </span>
                  </label>
                  <div class="action-row">
                    <Button
                      :label="`Consultar ${activeCpfProviderName}`"
                      icon="i-lucide-search"
                      :is-loading="isConsultingCpf"
                      :disabled="!canConsultCpf"
                      @click="consultCpf"
                    />
                    <span>HTTP 201 no sucesso</span>
                  </div>
                  <div v-if="cpfQueryResponse" class="response-panel">
                    <div class="code-card-header">
                      <h3>Resposta da API</h3>
                      <span
                        :class="
                          cpfQueryResponse.ok ? 'success-badge' : 'error-badge'
                        "
                        >HTTP
                        {{ cpfQueryResponse.status || 'erro de rede' }}</span
                      >
                    </div>
                    <pre class="code-block max-h-80"><code>{{
                    JSON.stringify(cpfQueryResponse.data, null, 2)
                  }}</code></pre>
                  </div>
                </section>

                <section class="doc-card">
                  <div class="section-heading">
                    <span class="section-number">03</span>
                    <div>
                      <h3>Respostas</h3>
                      <p>Códigos HTTP e campos retornados.</p>
                    </div>
                  </div>
                  <div class="response-grid">
                    <div
                      v-for="item in cpfQueryResponseCodes"
                      :key="item[0]"
                      class="response-item"
                    >
                      <code>{{ item[0] }}</code>
                      <span>{{ item[1] }}</span>
                    </div>
                  </div>
                  <div class="field-chips">
                    <code v-for="field in cpfQueryResponseFields" :key="field">
                      {{ field }}
                    </code>
                  </div>
                </section>
              </div>

              <div
                v-else-if="activeEndpoint === 'messages'"
                class="endpoint-content"
              >
                <header class="endpoint-header">
                  <div class="endpoint-title-row">
                    <span
                      class="endpoint-large-icon i-lucide-message-square-share"
                    />
                    <div class="min-w-0">
                      <div class="mb-1 flex flex-wrap items-center gap-2">
                        <span class="method-badge">POST</span>
                        <span class="status-badge">Disponível</span>
                      </div>
                      <h2>Envio externo de mensagens</h2>
                      <p>
                        Envie textos, templates oficiais e mensagens interativas
                        por WhatsApp ou SMS. Conexões por QR Code permitem envio
                        de texto.
                      </p>
                    </div>
                  </div>
                  <div class="endpoint-row">
                    <code class="min-w-0 flex-1 break-all">{{ endpoint }}</code>
                    <Button
                      label="Copiar URL"
                      icon="i-lucide-copy"
                      variant="ghost"
                      color="slate"
                      size="sm"
                      @click="copyCode(endpoint, 'URL')"
                    />
                  </div>
                  <div class="inbox-picker">
                    <div>
                      <strong>Caixa usada nos exemplos</strong>
                      <span
                        >Atualiza o <code>inbox_id</code> automaticamente.</span
                      >
                    </div>
                    <ComboBox
                      v-model="selectedInboxId"
                      :options="inboxOptions"
                      :disabled="
                        inboxUIFlags.isFetching || !inboxOptions.length
                      "
                      :placeholder="
                        inboxUIFlags.isFetching
                          ? 'Carregando caixas...'
                          : 'Selecione WhatsApp, QR Code ou SMS'
                      "
                      search-placeholder="Buscar caixa..."
                      empty-state="Nenhuma caixa compatível encontrada"
                    />
                  </div>
                  <div v-if="selectedInbox" class="selection-summary">
                    <span class="i-lucide-circle-check size-4 text-n-teal-10" />
                    <strong>{{ selectedInbox.name }}</strong>
                    <span>{{ channelLabel(selectedInbox) }}</span>
                    <code>inbox_id: {{ selectedInbox.id }}</code>
                  </div>
                  <p
                    v-if="isQrCodeInbox(selectedInbox)"
                    class="mt-3 text-sm text-n-slate-11"
                  >
                    Esta caixa usa WhatsApp por QR Code. Envie mensagens de
                    texto com a conexão ativa; templates oficiais e mensagens
                    interativas não estão disponíveis nessa conexão.
                  </p>
                </header>

                <nav class="section-tabs" aria-label="Seções do endpoint">
                  <Button
                    v-for="item in messageSections"
                    :key="item.value"
                    :label="item.label"
                    :icon="item.icon"
                    color="slate"
                    size="sm"
                    :variant="
                      activeMessageSection === item.value ? 'faded' : 'ghost'
                    "
                    @click="activeMessageSection = item.value"
                  />
                </nav>

                <section
                  v-if="activeMessageSection === 'overview'"
                  class="doc-card"
                >
                  <div class="section-heading">
                    <span class="section-number">01</span>
                    <div>
                      <h3>Campos da requisição</h3>
                      <p>
                        O conteúdo pode ser texto, template ou estrutura
                        interativa.
                      </p>
                    </div>
                  </div>
                  <div class="field-list two-columns">
                    <div
                      v-for="field in fields"
                      :key="field.name"
                      class="field-item"
                    >
                      <div>
                        <code>{{ field.name }}</code>
                        <span
                          :class="{
                            required: field.type.includes('obrigatório'),
                          }"
                          >{{ field.type }}</span
                        >
                      </div>
                      <p>{{ field.description }}</p>
                    </div>
                  </div>
                  <div class="info-callout">
                    <span class="i-lucide-message-circle-warning size-5" />
                    <p>
                      Mensagens interativas exigem WhatsApp Cloud e janela de
                      atendimento aberta. Fora de 24 horas, use um template
                      oficial aprovado.
                    </p>
                  </div>
                </section>

                <section
                  v-else-if="activeMessageSection === 'examples'"
                  class="code-card"
                >
                  <div class="example-toolbar">
                    <div>
                      <span class="eyebrow">Exemplos atualizados</span>
                      <h3>{{ activeExampleTitle }}</h3>
                    </div>
                    <div class="flex flex-wrap gap-1">
                      <Button
                        v-for="tab in exampleTabs"
                        :key="tab.value"
                        :label="tab.label"
                        color="slate"
                        size="sm"
                        :variant="
                          activeExample === tab.value ? 'faded' : 'ghost'
                        "
                        @click="activeExample = tab.value"
                      />
                    </div>
                  </div>
                  <div
                    v-if="activeExample === 'interactive'"
                    class="interactive-selector"
                  >
                    <div>
                      <strong>Tipo de mensagem interativa</strong>
                      <span>{{ currentInteractiveExample.description }}</span>
                    </div>
                    <ComboBox
                      v-model="selectedInteractiveExample"
                      :options="interactiveOptions"
                      placeholder="Selecione um tipo"
                    />
                  </div>
                  <div class="code-card-header">
                    <span
                      >Substitua <code>USER_API_KEY</code> antes de usar.</span
                    >
                    <Button
                      label="Copiar código"
                      icon="i-lucide-copy"
                      variant="ghost"
                      color="slate"
                      size="sm"
                      @click="copyCode(activeExampleCode, 'Código')"
                    />
                  </div>
                  <pre
                    class="code-block code-block-large"
                  ><code>{{ activeExampleCode }}</code></pre>
                </section>

                <section
                  v-else-if="activeMessageSection === 'console'"
                  class="console-card"
                >
                  <div class="section-heading">
                    <span class="section-number">02</span>
                    <div>
                      <h3>Console de teste</h3>
                      <p>
                        Execute uma chamada real. A API key não é salva pela
                        página.
                      </p>
                    </div>
                    <span class="console-badge">
                      <span class="i-lucide-terminal size-3.5" /> Console
                    </span>
                  </div>
                  <div class="form-grid">
                    <Input
                      v-model="apiKey"
                      type="password"
                      label="API key do usuário"
                      placeholder="Cole a API key"
                      show-password-toggle
                      autocomplete="off"
                    />
                    <Input
                      :model-value="phoneNumber"
                      label="Telefone do destinatário"
                      placeholder="+55 (11) 99999-9999"
                      autocomplete="off"
                      message="Inclua código do país e DDD."
                      @update:model-value="updateMessagePhone"
                    />
                    <Input
                      v-model="contactName"
                      label="Nome do contato (opcional)"
                      placeholder="Maria Silva"
                      autocomplete="off"
                    />
                    <div class="grid gap-1">
                      <label class="mb-0.5 text-heading-3 text-n-slate-12">
                        Status inicial da conversa
                      </label>
                      <ComboBox
                        v-model="conversationStatus"
                        :options="statusOptions"
                        placeholder="Selecione o status"
                      />
                    </div>
                  </div>
                  <TextArea
                    v-model="messageContent"
                    label="Mensagem"
                    placeholder="Digite a mensagem de teste"
                    :max-length="4096"
                    show-character-count
                    resize
                    min-height="7rem"
                  />
                  <label class="real-send-warning">
                    <input
                      v-model="hasConfirmedRealSend"
                      type="checkbox"
                      class="mt-0.5 size-4 accent-n-brand"
                    />
                    <span>
                      Confirmo que este teste enviará uma mensagem real e poderá
                      gerar cobrança do provedor.
                    </span>
                  </label>
                  <div class="action-row">
                    <Button
                      label="Enviar mensagem de teste"
                      icon="i-lucide-send"
                      :is-loading="isSending"
                      :disabled="!canSendTest"
                      @click="sendTestMessage"
                    />
                    <span v-if="!selectedInboxId">
                      Selecione uma caixa para habilitar o teste.
                    </span>
                  </div>
                  <div v-if="testResponse" class="response-panel">
                    <div class="code-card-header">
                      <h3>Resposta da API</h3>
                      <span
                        :class="
                          testResponse.ok ? 'success-badge' : 'error-badge'
                        "
                        >HTTP {{ testResponse.status || 'erro de rede' }}</span
                      >
                    </div>
                    <pre class="code-block max-h-80"><code>{{
                    JSON.stringify(testResponse.data, null, 2)
                  }}</code></pre>
                  </div>
                </section>

                <section v-else class="doc-card">
                  <div class="section-heading">
                    <span class="section-number">03</span>
                    <div>
                      <h3>Respostas e diagnóstico</h3>
                      <p>
                        Use o status HTTP para orientar o tratamento do erro.
                      </p>
                    </div>
                  </div>
                  <div class="response-grid">
                    <div
                      v-for="item in responseCodes"
                      :key="item[0]"
                      class="response-item"
                    >
                      <code>{{ item[0] }}</code>
                      <span>{{ item[1] }}</span>
                    </div>
                  </div>
                  <div>
                    <h4 class="mb-2 text-sm font-semibold text-n-slate-12">
                      Campos retornados no sucesso
                    </h4>
                    <div class="field-chips">
                      <code v-for="field in responseFields" :key="field">
                        {{ field }}
                      </code>
                    </div>
                  </div>
                  <div class="info-callout">
                    <span class="i-lucide-stethoscope size-5" />
                    <p>
                      Em caso de erro, confira a API key, o acesso à caixa, o
                      formato internacional do telefone e a conexão do canal.
                    </p>
                  </div>
                </section>
              </div>

              <div v-else class="endpoint-content">
                <header class="endpoint-header">
                  <div class="endpoint-title-row">
                    <span
                      class="endpoint-large-icon i-lucide-message-circle-more"
                    />
                    <div class="min-w-0">
                      <div class="mb-1 flex flex-wrap items-center gap-2">
                        <span class="method-badge">POST</span>
                        <span class="status-badge">Disponível</span>
                      </div>
                      <h2>Disparo externo de SMS</h2>
                      <p>
                        Envie um SMS individual ou em lote pela integração
                        Kolmeya, usando o saldo de créditos da conta.
                      </p>
                    </div>
                  </div>
                  <div class="endpoint-row">
                    <code class="min-w-0 flex-1 break-all">
                      {{ smsEndpoint }}
                    </code>
                    <Button
                      label="Copiar URL"
                      icon="i-lucide-copy"
                      variant="ghost"
                      color="slate"
                      size="sm"
                      @click="copyCode(smsEndpoint, 'URL')"
                    />
                  </div>
                </header>

                <div class="content-grid">
                  <section class="doc-card">
                    <div class="section-heading">
                      <span class="section-number">01</span>
                      <div>
                        <h3>Corpo da requisição</h3>
                        <p>Envie recipient ou recipients junto da mensagem.</p>
                      </div>
                    </div>
                    <div class="field-list">
                      <div
                        v-for="field in smsFields"
                        :key="field.name"
                        class="field-item"
                      >
                        <div>
                          <code>{{ field.name }}</code>
                          <span
                            :class="{
                              required: field.type.includes('obrigatório'),
                            }"
                            >{{ field.type }}</span
                          >
                        </div>
                        <p>{{ field.description }}</p>
                      </div>
                    </div>
                    <div class="info-callout">
                      <span class="i-lucide-coins size-5" />
                      <p>
                        A cobrança considera os segmentos por destinatário.
                        GSM-7 usa 160 caracteres no primeiro segmento e 153 nos
                        seguintes; Unicode usa 70 e 67. O limite é de 6
                        segmentos. “Pix” e “0800” não são permitidos.
                      </p>
                    </div>
                  </section>

                  <section class="code-card">
                    <div class="code-card-header">
                      <div>
                        <span class="eyebrow">Criar disparo</span>
                        <h3>cURL</h3>
                      </div>
                      <Button
                        label="Copiar"
                        icon="i-lucide-copy"
                        variant="ghost"
                        color="slate"
                        size="sm"
                        @click="copyCode(smsCurlExample, 'cURL')"
                      />
                    </div>
                    <pre
                      class="code-block"
                    ><code>{{ smsCurlExample }}</code></pre>
                    <p class="code-hint">
                      <span class="i-lucide-clock-3 size-4" />
                      HTTP 202 confirma o enfileiramento. Guarde o public_id
                      para acompanhar entrega, falha e consumo de créditos.
                    </p>
                  </section>
                </div>

                <section class="code-card">
                  <div class="code-card-header">
                    <div>
                      <span class="eyebrow">Consultar processamento</span>
                      <h3>GET por public_id</h3>
                    </div>
                    <Button
                      label="Copiar"
                      icon="i-lucide-copy"
                      variant="ghost"
                      color="slate"
                      size="sm"
                      @click="copyCode(smsStatusCurlExample, 'cURL')"
                    />
                  </div>
                  <div class="endpoint-row">
                    <code class="min-w-0 flex-1 break-all">
                      {{ smsStatusEndpoint }}
                    </code>
                    <span class="method-badge">GET</span>
                  </div>
                  <pre
                    class="code-block"
                  ><code>{{ smsStatusCurlExample }}</code></pre>
                </section>

                <section class="doc-card">
                  <h3>Reenviar falhas HTTP 500</h3>
                  <p>
                    <code>POST {{ smsStatusEndpoint }}/retry_failed</code>
                  </p>
                  <p>
                    Envie <code>{}</code> para reenviar todos os destinatários
                    elegíveis do disparo. Para somente um, informe
                    <code>recipient_id</code> com o public_id do destinatário
                    retornado na consulta.
                  </p>
                  <pre
                    class="code-block"
                  ><code>{{ smsRetryCurlExample }}</code></pre>
                  <p>
                    Somente destinatários failed ou invalid com HTTP 500 e sem
                    nova tentativa registrada são elegíveis. HTTP 500 histórico
                    no motivo também é reconhecido. Outros erros, entregas
                    confirmadas e campanhas ainda na fila ou enviando não são
                    reenviados.
                  </p>
                  <p>
                    O retorno 202 contém uma nova campanha com novos public_ids,
                    mesma mensagem e mesmas excluded_labels. O original fica
                    preservado. Cada destinatário original admite uma nova
                    tentativa; chamadas repetidas ou concorrentes não duplicam a
                    reserva. Uma falha 500 na nova campanha pode ser reenviada
                    por seu próprio ID.
                  </p>
                  <p>
                    O saldo é validado e reservado novamente. Contatos excluídos
                    antes da criação não são cobrados; bloqueios encontrados na
                    saída da fila são estornados. Se todos forem excluídos, a
                    resposta é 422, sem criar campanha nem consumir créditos.
                    Etiquetas desconhecidas também retornam 422.
                  </p>
                  <p>
                    A consulta informa <code>excluded_labels</code>,
                    <code>excluded_recipients_count</code> (exclusões antes da
                    criação) e <code>retryable_count</code>. Cada destinatário
                    inclui <code>provider_http_status</code>,
                    <code>retryable</code> e
                    <code>retried_campaign_id</code> (public_id da nova
                    campanha). Bloqueios na fila usam
                    <code>provider_status=excluded_label</code>. Sem
                    destinatários elegíveis, o reenvio responde 422; uma
                    campanha fora da conta responde 404. A autenticação e as
                    permissões são as mesmas do envio.
                  </p>
                </section>

                <section class="doc-card">
                  <div class="section-heading">
                    <span class="section-number">02</span>
                    <div>
                      <h3>Respostas e status</h3>
                      <p>
                        O status começa em queued e é atualizado de forma
                        assíncrona pelos retornos da operadora.
                      </p>
                    </div>
                  </div>
                  <div class="response-grid">
                    <div
                      v-for="item in smsResponseCodes"
                      :key="item[0]"
                      class="response-item"
                    >
                      <code>{{ item[0] }}</code>
                      <span>{{ item[1] }}</span>
                    </div>
                  </div>
                  <div class="field-chips">
                    <code v-for="field in smsResponseFields" :key="field">
                      {{ field }}
                    </code>
                  </div>
                  <div class="info-callout">
                    <span class="i-lucide-list-checks size-5" />
                    <p>
                      Estados possíveis da campanha: queued, sending, submitted,
                      partially_delivered, delivered e failed. Cada destinatário
                      retorna seu próprio status e eventual mensagem de erro.
                    </p>
                  </div>
                </section>
              </div>
            </main>
          </div>
        </div>
      </section>
    </template>
  </SettingsLayout>
</template>

<style scoped>
.api-page {
  @apply mx-auto grid w-full max-w-7xl gap-4 pb-10;
}
.api-hero {
  @apply flex flex-col gap-5 overflow-hidden rounded-xl border border-n-brand/30 bg-n-brand/5 p-5 sm:p-6 lg:flex-row lg:items-center;
}
.api-hero h1 {
  @apply mb-0 mt-1 text-2xl font-semibold text-n-slate-12;
}
.api-hero p {
  @apply mb-0 mt-2 max-w-3xl text-sm text-n-slate-11;
}
.eyebrow {
  @apply text-xs font-semibold uppercase tracking-wide text-n-brand;
}
.base-url-card {
  @apply min-w-0 rounded-lg border border-n-weak bg-n-solid-2 p-3 lg:min-w-64;
}
.base-url-card > span {
  @apply text-xs text-n-slate-10;
}
.base-url-card div {
  @apply mt-1 flex min-w-0 items-center gap-2;
}
.base-url-card code {
  @apply truncate text-sm font-medium text-n-slate-12;
}
.api-meta-bar {
  @apply grid overflow-hidden rounded-xl border border-n-weak bg-n-solid-2 sm:grid-cols-3;
}
.meta-item {
  @apply flex items-center gap-2 border-b border-n-weak px-4 py-3 text-xs text-n-slate-11 last:border-b-0 sm:border-b-0 sm:border-r sm:last:border-r-0;
}
.meta-item strong {
  @apply mr-1 text-n-slate-12;
}
.security-note {
  @apply flex items-start gap-3 rounded-lg border border-n-amber-6 bg-n-amber-3/30 p-4 text-sm text-n-slate-11;
}
.security-note p {
  @apply mb-0;
}
.docs-workspace {
  @apply grid min-w-0 items-start gap-4 xl:grid-cols-[290px_minmax(0,1fr)];
}
.endpoint-sidebar {
  @apply grid min-w-0 gap-2 overflow-hidden rounded-xl border border-n-weak bg-n-solid-2 p-3 xl:sticky xl:top-4;
}
.sidebar-heading {
  @apply flex items-center justify-between px-2 pb-1;
}
.sidebar-heading > span {
  @apply text-xs font-semibold uppercase tracking-wide text-n-slate-11;
}
.sidebar-heading small {
  @apply text-xs text-n-slate-10;
}
.endpoint-selector {
  @apply flex w-full min-w-0 max-w-full items-center gap-3 overflow-hidden rounded-lg border border-transparent p-3 text-left transition hover:bg-n-alpha-2;
}
.endpoint-selector.active {
  @apply border-n-brand/30 bg-n-brand/10 ring-1 ring-n-brand/10;
}
.endpoint-selector strong,
.endpoint-selector small {
  @apply block truncate;
}
.endpoint-selector strong {
  @apply text-sm text-n-slate-12;
}
.endpoint-selector small {
  @apply mt-0.5 text-xs text-n-slate-10;
}
.endpoint-icon {
  @apply size-5 shrink-0 text-n-slate-10;
}
.endpoint-selector.active .endpoint-icon {
  @apply text-n-brand;
}
.quick-guide {
  @apply mt-2 border-t border-n-weak px-2 pt-4;
}
.quick-guide ol {
  @apply mb-0 mt-3 grid list-none gap-3;
}
.quick-guide li {
  @apply flex items-center gap-2 text-xs text-n-slate-11;
}
.quick-guide li span {
  @apply grid size-5 shrink-0 place-items-center rounded-full bg-n-slate-3 text-xxs font-semibold text-n-slate-12;
}
.endpoint-content {
  @apply grid min-w-0 gap-4;
}
.endpoint-header,
.doc-card,
.console-card,
.code-card {
  @apply min-w-0 rounded-xl border border-n-weak bg-n-solid-2 p-4 sm:p-5;
}
.endpoint-header {
  @apply grid gap-4;
}
.endpoint-title-row {
  @apply flex items-start gap-3;
}
.endpoint-large-icon {
  @apply grid size-10 shrink-0 place-items-center rounded-lg bg-n-brand/10 p-2.5 text-n-brand;
}
.endpoint-header h2 {
  @apply m-0 text-xl font-semibold text-n-slate-12;
}
.endpoint-header p {
  @apply mb-0 mt-1 text-sm text-n-slate-11;
}
.method-badge {
  @apply shrink-0 rounded-md bg-n-blue-4 px-2 py-1 text-xxs font-semibold text-n-blue-11;
}
.status-badge {
  @apply rounded-full bg-n-teal-4 px-2 py-1 text-xxs font-semibold text-n-teal-11;
}
.endpoint-row {
  @apply flex flex-wrap items-center gap-2 rounded-lg border border-n-weak bg-n-slate-3 p-2.5 text-sm text-n-slate-12;
}
.content-grid {
  @apply grid min-w-0 gap-4 xl:grid-cols-[minmax(0,1.1fr)_minmax(360px,0.9fr)];
}
.doc-card,
.console-card,
.code-card {
  @apply grid gap-4;
}
.section-heading {
  @apply flex flex-wrap items-start gap-3;
}
.section-heading > div {
  @apply min-w-0 flex-1;
}
.section-heading h3,
.code-card h3 {
  @apply m-0 text-base font-semibold text-n-slate-12;
}
.section-heading p {
  @apply mb-0 mt-1 text-sm text-n-slate-10;
}
.section-number {
  @apply grid size-8 shrink-0 place-items-center rounded-lg bg-n-slate-3 text-xs font-semibold text-n-slate-11;
}
.field-list {
  @apply overflow-hidden rounded-lg border border-n-weak;
}
.field-list.two-columns {
  @apply grid lg:grid-cols-2;
}
.field-item {
  @apply grid gap-2 border-b border-n-weak p-3 last:border-b-0;
}
.two-columns .field-item {
  @apply lg:border-r lg:[&:nth-child(2n)]:border-r-0 lg:[&:nth-last-child(-n+2)]:border-b-0;
}
.field-item > div {
  @apply flex flex-wrap items-center justify-between gap-2;
}
.field-item code {
  @apply text-xs font-semibold text-n-slate-12;
}
.field-item span {
  @apply rounded bg-n-slate-3 px-2 py-0.5 text-xxs text-n-slate-10;
}
.field-item span.required {
  @apply bg-n-amber-4 text-n-amber-11;
}
.field-item p {
  @apply mb-0 text-xs leading-5 text-n-slate-11;
}
.code-card {
  @apply content-start bg-n-slate-2;
}
.code-card-header,
.example-toolbar {
  @apply flex flex-wrap items-center justify-between gap-3;
}
.code-card-header {
  @apply text-xs text-n-slate-10;
}
.code-block {
  @apply m-0 max-w-full overflow-auto rounded-lg bg-n-slate-3 p-4 text-xs leading-5 text-n-slate-12;
}
.code-block-large {
  @apply max-h-[560px] min-h-72;
}
.code-hint {
  @apply mb-0 flex items-start gap-2 rounded-lg bg-n-alpha-2 p-3 text-xs text-n-slate-10;
}
.console-card {
  @apply border-n-brand/30;
}
.console-badge {
  @apply inline-flex items-center gap-1 rounded-md bg-n-brand/10 px-2 py-1 text-xs font-semibold text-n-brand;
}
.form-grid {
  @apply grid gap-4 lg:grid-cols-2;
}
.real-send-warning {
  @apply flex cursor-pointer items-start gap-3 rounded-lg border border-n-amber-6 bg-n-amber-3/30 p-3 text-sm text-n-slate-12;
}
.action-row {
  @apply flex flex-wrap items-center gap-3;
}
.action-row > span {
  @apply text-xs text-n-slate-10;
}
.response-panel {
  @apply grid gap-3 rounded-lg border border-n-weak bg-n-alpha-1 p-3;
}
.success-badge {
  @apply rounded-md bg-n-teal-4 px-2 py-1 text-xs font-semibold text-n-teal-11;
}
.error-badge {
  @apply rounded-md bg-n-ruby-4 px-2 py-1 text-xs font-semibold text-n-ruby-11;
}
.response-grid {
  @apply grid gap-2 sm:grid-cols-2;
}
.response-item {
  @apply grid grid-cols-[48px_1fr] items-start gap-2 rounded-lg border border-n-weak p-3 text-xs text-n-slate-11;
}
.response-item code {
  @apply font-semibold text-n-slate-12;
}
.field-chips {
  @apply flex flex-wrap gap-2;
}
.field-chips code {
  @apply rounded-md bg-n-slate-3 px-2 py-1 text-xs text-n-slate-12;
}
.inbox-picker {
  @apply grid items-center gap-3 rounded-lg border border-n-weak bg-n-alpha-1 p-3 lg:grid-cols-[minmax(0,1fr)_minmax(300px,0.8fr)];
}
.inbox-picker strong,
.inbox-picker span {
  @apply block;
}
.inbox-picker strong {
  @apply text-sm text-n-slate-12;
}
.inbox-picker span {
  @apply mt-1 text-xs text-n-slate-10;
}
.selection-summary {
  @apply flex flex-wrap items-center gap-2 rounded-lg bg-n-teal-3/40 px-3 py-2 text-xs text-n-slate-11;
}
.selection-summary code {
  @apply rounded bg-n-solid-2 px-2 py-1 text-n-slate-12;
}
.section-tabs {
  @apply flex flex-wrap gap-1 rounded-xl border border-n-weak bg-n-solid-2 p-2;
}
.example-toolbar {
  @apply border-b border-n-weak pb-4;
}
.interactive-selector {
  @apply grid items-center gap-3 rounded-lg border border-n-weak bg-n-alpha-1 p-3 lg:grid-cols-[minmax(0,1fr)_280px];
}
.interactive-selector strong,
.interactive-selector span {
  @apply block;
}
.interactive-selector strong {
  @apply text-sm text-n-slate-12;
}
.interactive-selector span {
  @apply mt-1 text-xs text-n-slate-10;
}
.info-callout {
  @apply flex items-start gap-3 rounded-lg border border-n-blue-6 bg-n-blue-3/30 p-4 text-n-blue-11;
}
.info-callout p {
  @apply mb-0 text-sm text-n-slate-11;
}
@media (max-width: 1279px) {
  .endpoint-sidebar {
    @apply grid-cols-2;
  }
  .sidebar-heading,
  .quick-guide {
    @apply col-span-2;
  }
  .quick-guide {
    @apply hidden;
  }
}
@media (max-width: 639px) {
  .endpoint-sidebar {
    @apply grid-cols-1;
  }
  .sidebar-heading {
    @apply col-span-1;
  }
}
</style>
